<template>
  <div class="stalls-page">
    <!-- 顶部导航 -->
    <header class="back-header">
      <div class="back-btn" @click="$router.back()">←</div>
      <h1>档口管理</h1>
      <div class="header-actions">
        <button class="add-btn" @click="openAddModal">新建档口 +</button>
      </div>
    </header>

    <!-- 内容 -->
    <div class="main-content">
      <!-- 搜索栏 -->
      <div class="search-bar">
        <input 
          type="text" 
          v-model="searchQuery"
          placeholder="搜索档口名称..."
          class="search-input"
          @input="handleSearch"
        />
      </div>

      <!-- 档口列表 -->
      <div class="stall-list">
        <div v-for="stall in filteredStalls" :key="stall.id" class="stall-card">
          <div class="stall-info">
            <div class="stall-name">{{ stall.customerName || stall.customerName || '未命名' }}</div>
            <div class="stall-address">📍 {{ stall.address || '无地址' }}</div>
            <div class="stall-contact">📞 {{ stall.contactPhone || '无联系方式' }}</div>
          </div>
          <div class="stall-actions">
            <button class="btn-link" @click="openLinkModal(stall)">🔗 关联</button>
            <button class="btn-edit" @click="editStall(stall)">编辑</button>
            <button class="btn-delete" @click="deleteStall(stall)">删除</button>
          </div>
        </div>

        <!-- 空状态 -->
        <div v-if="filteredStalls.length === 0" class="empty-state">
          <div class="empty-icon">🏪</div>
          <p>暂无档口</p>
        </div>
      </div>

      <!-- 分页 -->
      <div v-if="totalPages > 1" class="pagination">
        <button :disabled="currentPage <= 1" @click="goPage(currentPage - 1)">‹ 上一页</button>
        <span class="page-info">{{ currentPage }} / {{ totalPages }} 页，共 {{ totalStalls }} 条</span>
        <button :disabled="currentPage >= totalPages" @click="goPage(currentPage + 1)">下一页 ›</button>
      </div>
    </div>

    <!-- 关联批发商弹窗 -->
    <div v-if="showLinkModal" class="modal-overlay" @click="closeLinkModal">
      <div class="modal modal-wide" @click.stop>
        <div class="modal-header">
          <div class="modal-title">🔗 关联批发商 - {{ linkStall && linkStall.customerName }}</div>
          <button class="modal-close" @click="closeLinkModal">×</button>
        </div>
        <div class="modal-body">
          <!-- 已关联的批发商 -->
          <div class="link-section">
            <h4>已关联的批发商</h4>
            <div v-if="linkedWholesalers.length === 0" class="link-empty">
              暂未关联任何批发商
            </div>
            <div v-for="w in linkedWholesalers" :key="w.mappingId" class="link-item">
              <div class="link-item-info">
                <div class="link-item-name">{{ w.companyName || '未命名' }}</div>
                <div class="link-item-detail">{{ w.wholesalerCode }} · {{ w.contactPerson || '-' }} · {{ w.contactPhone || '-' }}</div>
              </div>
              <button class="btn-unlink" @click="removeLink(w)">取消关联</button>
            </div>
          </div>

          <!-- 添加新关联 -->
          <div class="link-section">
            <h4>添加批发商关联</h4>
            <div class="link-search-row">
              <input
                v-model="wholesalerSearch"
                placeholder="搜索批发商名称..."
                class="form-input link-search-input"
                @input="searchWholesalers"
              />
            </div>
            <div class="wholesaler-list">
              <div
                v-for="w in availableWholesalers"
                :key="w.id"
                class="wholesaler-option"
                :class="{ disabled: isAlreadyLinked(w.id) }"
                @click="!isAlreadyLinked(w.id) && addLink(w)"
              >
                <div class="wholesaler-option-info">
                  <span class="wholesaler-option-name">{{ w.companyName || '未命名' }}</span>
                  <span class="wholesaler-option-code">{{ w.wholesalerCode }}</span>
                </div>
                <span v-if="isAlreadyLinked(w.id)" class="linked-badge">已关联</span>
                <span v-else class="add-badge">+ 关联</span>
              </div>
              <div v-if="availableWholesalers.length === 0" class="link-empty">
                未找到批发商
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-confirm" @click="closeLinkModal" style="flex:1">关闭</button>
        </div>
      </div>
    </div>

    <!-- 新增/编辑弹窗 -->
    <div v-if="showAddModal || showEditModal" class="modal-overlay" @click="closeModal">
      <div class="modal" @click.stop>
        <div class="modal-header">
          <div class="modal-title">{{ showAddModal ? '新建档口' : '编辑档口' }}</div>
          <button class="modal-close" @click="closeModal">×</button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label>档口名称 *</label>
            <input 
              v-model="formData.customerName"
              placeholder="请输入档口名称"
              class="form-input"
            />
          </div>
          
          <div class="form-group">
            <label>档口地址</label>
            <input 
              v-model="formData.address"
              placeholder="请输入档口地址"
              class="form-input"
            />
          </div>
          
          <div class="form-group">
            <label>所属区域</label>
            <select v-model="formData.regionCode" class="form-input">
              <option value="">请选择区域</option>
              <option v-for="region in regions" :key="region.id" :value="region.regionCode">
                {{ region.regionName }} ({{ region.regionCode }})
              </option>
            </select>
          </div>
          
          <div class="form-group">
            <label>联系人</label>
            <input 
              v-model="formData.contactPerson"
              placeholder="请输入联系人姓名"
              class="form-input"
            />
          </div>
          
          <div class="form-group">
            <label>联系电话</label>
            <input 
              v-model="formData.contactPhone"
              placeholder="请输入联系电话"
              class="form-input"
            />
          </div>

          <div class="form-group">
            <label>税号 (GST/UEN)</label>
            <input 
              v-model="formData.taxNo"
              placeholder="例：M2-1234567-X 或 UEN 号码"
              class="form-input"
            />
          </div>
          
          <div class="form-group">
            <label>备注</label>
            <textarea 
              v-model="formData.remark"
              placeholder="请输入备注"
              class="form-textarea"
              rows="3"
            ></textarea>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-cancel" @click="closeModal">取消</button>
          <button class="btn-confirm" @click="saveStall">保存</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/services/api';

export default {
  name: 'AdminStalls',
  data() {
    return {
      stalls: [],
      filteredStalls: [],
      regions: [],
      searchQuery: '',
      showAddModal: false,
      showEditModal: false,
      // 分页
      currentPage: 1,
      totalPages: 0,
      totalStalls: 0,
      pageSize: 20,
      // 关联批发商
      showLinkModal: false,
      linkStall: null,
      linkedWholesalers: [],
      allWholesalers: [],
      availableWholesalers: [],
      wholesalerSearch: '',
      formData: {
        id: null,
        customerName: '',
        address: '',
        regionCode: '',
        contactPerson: '',
        contactPhone: '',
        taxNo: '',
        remark: ''
      }
    };
  },
  mounted() {
    this.loadRegions();
    this.loadStalls();
  },
  methods: {
    // 加载区域列表
    async loadRegions() {
      try {
        const res = await api.get('/admin/regions/enabled');
        console.log('🔵 加载区域 - 原始响应:', res);
        
        // ✅ API 拦截器已经返回 response.data，所以 res 直接是数组
        this.regions = Array.isArray(res) ? res : (res.data || []);
        console.log('✅ regions 列表:', this.regions);
        console.log('✅ regions sample:', this.regions[0]);
      } catch (error) {
        console.error('❌ 加载区域失败:', error);
      }
    },

    async loadStalls(page = 1) {
      try {
        const response = await api.post('/customers/query', {
          pageNo: page,
          pageSize: this.pageSize
        });
        
        let data = [];
        let totalElements = 0;
        let totalPages = 0;
        
        if (response.content && Array.isArray(response.content)) {
          data = response.content;
          totalElements = response.totalElements || data.length;
          totalPages = response.totalPages || Math.ceil(totalElements / this.pageSize);
        } else if (Array.isArray(response)) {
          data = response;
          totalElements = data.length;
          totalPages = 1;
        } else if (response.data?.content && Array.isArray(response.data.content)) {
          data = response.data.content;
          totalElements = response.data.totalElements || data.length;
          totalPages = response.data.totalPages || Math.ceil(totalElements / this.pageSize);
        }
        
        this.stalls = data;
        this.filteredStalls = data;
        this.currentPage = page;
        this.totalStalls = totalElements;
        this.totalPages = totalPages || 1;
      } catch (error) {
        console.error('❌ 加载档口失败:', error);
      }
    },

    goPage(page) {
      if (page < 1 || page > this.totalPages) return;
      this.loadStalls(page);
    },

    openAddModal() {
      this.formData = {
        id: null,
        customerName: '',
        address: '',
        regionCode: '', // ✅补上
        contactPerson: '',
        contactPhone: '',
        remark: ''
      };
      this.showAddModal = true;
    },
    
    async handleSearch() {
      if (!this.searchQuery.trim()) {
        this.loadStalls(1);
        return;
      }
      try {
        const response = await api.post('/customers/query', {
          pageNo: 1,
          pageSize: this.pageSize,
          customerName: this.searchQuery.trim()
        });
        
        let data = [];
        let totalElements = 0;
        let totalPages = 0;
        
        if (response.content && Array.isArray(response.content)) {
          data = response.content;
          totalElements = response.totalElements || data.length;
          totalPages = response.totalPages || Math.ceil(totalElements / this.pageSize);
        } else if (Array.isArray(response)) {
          data = response;
          totalElements = data.length;
          totalPages = 1;
        } else if (response.data?.content && Array.isArray(response.data.content)) {
          data = response.data.content;
          totalElements = response.data.totalElements || data.length;
          totalPages = response.data.totalPages || Math.ceil(totalElements / this.pageSize);
        }
        
        this.filteredStalls = data;
        this.currentPage = 1;
        this.totalStalls = totalElements;
        this.totalPages = totalPages || 1;
      } catch (error) {
        console.error('搜索档口失败:', error);
      }
    },
    
    editStall(stall) {
      console.log('🔵 编辑档口:', stall);
      console.log('🔵 档口 customerCode:', stall.customerCode);
      console.log('🔵 档口 regionCode:', stall.regionCode);
      console.log('🔵 档口 regionName:', stall.regionName);
      
      this.formData = {
        id: stall.id,
        customerName: stall.customerName || stall.customerName || '', // ✅ 优先取 customerName
        address: stall.companyAddress || '',
        regionCode: stall.regionCode || '',
        contactPerson: stall.contactPerson || '',
        contactPhone: stall.phone || '',
        taxNo: stall.taxNo || '',
        remark: stall.remark || '',
        customerCode: stall.customerCode || '' // ✅ 保存原始的 customerCode
      };
      console.log('🔵 formData:', this.formData);
      this.showEditModal = true;
    },
    
    async saveStall() {
      if (!this.formData.customerName) {
        alert('请输入档口名称');
        return;
      }
      
      console.log('🔵 保存档口 - formData:', this.formData);
      console.log('🔵 保存档口 - formData.regionCode:', this.formData.regionCode);
      console.log('🔵 regions 列表:', this.regions);
      
      // ✅从区域列表反查名称
      const region = this.regions.find(r => r.regionCode === this.formData.regionCode);
      const regionName = region?.regionName || '';
      console.log('🔵 反查到的区域:', region);
      console.log('🔵 regionName:', regionName);
      
      try {
        // 转换为后端需要的字段名（CustomerDTO）
        const payload = {
          id: this.formData.id,
          customerName: this.formData.customerName,
          customerName: this.formData.customerName,
          regionCode: this.formData.regionCode || null,
          regionName: regionName || null,
          contactPerson: this.formData.contactPerson,
          phone: this.formData.contactPhone,
          taxNo: this.formData.taxNo || null,
          companyAddress: this.formData.address,
          remark: this.formData.remark,
          customerType: 'stall', // 标记为档口类型
          status: 0 // 正常状态
        };
        
        // customerCode 处理：创建时生成新的，更新时使用原有的
        if (!this.formData.id) {
          // 创建：生成新的 customerCode
          payload.customerCode = 'STALL_' + Date.now();
        } else {
          // 更新：使用原始的 customerCode
          payload.customerCode = this.formData.customerCode;
        }
        
        console.log('🔵 保存 payload:', payload);
        console.log('🔵 payload.regionCode:', payload.regionCode);
        console.log('🔵 payload.regionName:', payload.regionName);
        
        if (this.formData.id) {
          // 更新
          console.log('🔵 更新档口 ID:', this.formData.id);
          console.log('🔵 更新 payload:', JSON.stringify(payload, null, 2));
          const response = await api.put(`/customers/${this.formData.id}`, payload);
          console.log('🔵 完整响应对象:', response);
          console.log('🔵 响应 data:', response.data);
          console.log('🔵 响应 regionCode:', response.data?.regionCode);
          console.log('🔵 响应 regionName:', response.data?.regionName);
          alert('✅ 更新成功');
        } else {
          // 创建
          console.log('🔵 创建新档口');
          const response = await api.post('/customers', payload);
          console.log('🔵 创建响应:', response);
          console.log('🔵 响应 regionCode:', response?.regionCode);
          console.log('🔵 响应 regionName:', response?.regionName);
          alert('✅ 创建成功');
        }
        
        this.closeModal();
        this.loadStalls();
      } catch (error) {
        console.error('❌ 保存失败:', error);
        console.error('❌ 错误响应:', error.response?.data);
        alert('操作失败：' + (error.response?.data?.message || error.message));
      }
    },
    
    async deleteStall(stall) {
      console.log('deleteStall 被调用，档口:', stall);
      console.log('档口 ID:', stall?.id, 'customerName:', stall?.customerName);
      
      // 检查数据是否有效
      if (!stall || !stall.id) {
        console.error('无效的档口数据:', stall);
        alert('❌ 档口数据无效，无法删除');
        return;
      }
      
      // 使用自定义确认（避免浏览器自动化环境取消 confirm）
      const confirmed = window.confirm(`确定要删除档口"${stall.customerName}"吗？\n\n（点击确定删除，取消则保留）`);
      console.log('用户确认:', confirmed);
      
      if (!confirmed) {
        console.log('用户取消删除');
        return;
      }
      
      try {
        console.log('发送删除请求，ID:', stall.id);
        const response = await api.delete(`/customers/${stall.id}`);
        console.log('删除响应:', response);
        alert('✅ 删除成功');
        this.loadStalls();
      } catch (error) {
        console.error('删除失败:', error);
        console.error('错误详情:', error.response?.data);
        alert('删除失败：' + (error.response?.data?.message || error.message));
      }
    },
    
    closeModal() {
      this.showAddModal = false;
      this.showEditModal = false;
      this.formData = {
        id: null,
        customerName: '',
        address: '',
        regionCode: '',
        contactPerson: '',
        contactPhone: '',
        remark: ''
      };
    },

    // ========== 关联批发商 ==========
    async openLinkModal(stall) {
      this.linkStall = stall;
      this.showLinkModal = true;
      this.wholesalerSearch = '';
      await Promise.all([
        this.loadLinkedWholesalers(),
        this.loadAllWholesalers()
      ]);
    },

    closeLinkModal() {
      this.showLinkModal = false;
      this.linkStall = null;
      this.linkedWholesalers = [];
      this.availableWholesalers = [];
    },

    async loadLinkedWholesalers() {
      try {
        const res = await api.get(`/admin/stalls/${this.linkStall.id}/wholesalers`);
        this.linkedWholesalers = Array.isArray(res) ? res : (res?.data || []);
      } catch (error) {
        console.error('加载关联批发商失败:', error);
      }
    },

    async loadAllWholesalers() {
      try {
        const res = await api.get('/admin/stalls/search-wholesalers');
        this.allWholesalers = Array.isArray(res) ? res : (res?.data || []);
        this.availableWholesalers = this.allWholesalers;
      } catch (error) {
        console.error('加载批发商列表失败:', error);
      }
    },

    searchWholesalers() {
      const kw = this.wholesalerSearch.trim().toLowerCase();
      if (!kw) {
        this.availableWholesalers = this.allWholesalers;
        return;
      }
      this.availableWholesalers = this.allWholesalers.filter(w =>
        (w.companyName || '').toLowerCase().includes(kw) ||
        (w.wholesalerCode || '').toLowerCase().includes(kw)
      );
    },

    isAlreadyLinked(wholesalerId) {
      return this.linkedWholesalers.some(w => w.wholesalerId === wholesalerId);
    },

    async addLink(wholesaler) {
      try {
        await api.post(`/admin/stalls/${this.linkStall.id}/wholesalers/${wholesaler.id}`);
        alert('✅ 关联成功');
        await this.loadLinkedWholesalers();
      } catch (error) {
        alert('关联失败: ' + (error.message || '未知错误'));
      }
    },

    async removeLink(w) {
      if (!window.confirm(`确定取消与批发商"${w.companyName}"的关联吗？`)) return;
      try {
        await api.delete(`/admin/stalls/${this.linkStall.id}/wholesalers/${w.wholesalerId}`);
        alert('✅ 已取消关联');
        await this.loadLinkedWholesalers();
      } catch (error) {
        alert('取消失败: ' + (error.message || '未知错误'));
      }
    }
  }
};
</script>

<style scoped>
.stalls-page {
  min-height: 100vh;
  background: #f5f5f5;
  padding-bottom: 80px;
}

.back-header {
  background: linear-gradient(135deg, #ffc069 0%, #fa8c16 100%);
  padding: calc(12px + env(safe-area-inset-top, 0px)) 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  box-shadow: 0 2px 8px rgba(250, 140, 22, 0.15);
}

.back-btn {
  position: absolute;
  left: 16px;
  font-size: 20px;
  color: white;
  cursor: pointer;
}

.back-header h1 {
  font-size: 16px;
  font-weight: 600;
  color: white;
  margin: 0;
}

.header-actions {
  position: absolute;
  right: 16px;
}

.add-btn {
  padding: 8px 16px;
  border: none;
  background: linear-gradient(135deg, #fa8c16, #d46b08);
  color: white;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.add-btn:hover {
  opacity: 0.9;
  transform: translateY(-1px);
}

.main-content {
  padding: 16px;
}

.search-bar {
  margin-bottom: 16px;
}

.search-input {
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 14px;
}

.stall-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.stall-card {
  background: white;
  border-radius: 10px;
  padding: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05);
}

.stall-info {
  flex: 1;
}

.stall-name {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
}

.stall-address,
.stall-contact {
  font-size: 13px;
  color: #666;
  margin-bottom: 4px;
}

.stall-actions {
  display: flex;
  gap: 8px;
}

.btn-edit,
.btn-delete {
  padding: 6px 16px;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-edit {
  background: #f0f0f0;
  color: #666;
}

.btn-edit:hover {
  background: #e6f4ff;
  color: #667eea;
}

.btn-delete {
  background: #fff1f0;
  color: #ff4d4f;
}

.btn-delete:hover {
  background: #ffccc7;
}

.empty-state {
  text-align: center;
  padding: 80px 20px;
  color: #999;
}

.empty-icon {
  font-size: 64px;
  margin-bottom: 16px;
}

/* 弹窗样式 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  visibility: visible !important;
  opacity: 1 !important;
}

.modal {
  background: white !important;
  border-radius: 12px !important;
  width: 90% !important;
  max-width: 500px !important;
  max-height: 80vh !important;
  overflow-y: auto !important;
  color: #333 !important;
}

.modal * {
  color: #333 !important;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #eee;
}

.modal-title {
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

.modal-close {
  background: none;
  border: none;
  font-size: 24px;
  color: #999;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-body {
  padding: 20px;
}

.form-group {
  margin-bottom: 16px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #333;
  font-size: 14px;
}

.form-input,
.form-textarea {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  font-family: inherit;
}

.form-input:focus,
.form-textarea:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.modal-footer {
  display: flex;
  gap: 12px;
  padding: 16px 20px;
  border-top: 1px solid #eee;
}

.btn-cancel,
.btn-confirm {
  flex: 1;
  padding: 12px 24px;
  border: none;
  border-radius: 6px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-cancel {
  background: #f0f0f0;
  color: #666;
}

.btn-cancel:hover {
  background: #e0e0e0;
}

.btn-confirm {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.btn-confirm:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}

/* 关联按钮 */
.btn-link {
  padding: 6px 16px;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  cursor: pointer;
  background: #e6f7ff;
  color: #1890ff;
  transition: all 0.2s;
}

.btn-link:hover {
  background: #bae7ff;
}

/* 关联弹窗 */
.modal-wide {
  max-width: 640px !important;
}

.link-section {
  margin-bottom: 20px;
}

.link-section h4 {
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin: 0 0 12px;
}

.link-empty {
  text-align: center;
  color: #999;
  padding: 16px;
  font-size: 13px;
}

.link-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  background: #f9f9f9;
  border-radius: 8px;
  margin-bottom: 8px;
}

.link-item-name {
  font-size: 14px;
  font-weight: 600;
  color: #333;
}

.link-item-detail {
  font-size: 12px;
  color: #888;
  margin-top: 2px;
}

.btn-unlink {
  padding: 4px 12px;
  border: 1px solid #ff4d4f;
  border-radius: 4px;
  background: white;
  color: #ff4d4f;
  font-size: 12px;
  cursor: pointer;
  white-space: nowrap;
}

.btn-unlink:hover {
  background: #fff1f0;
}

.link-search-row {
  margin-bottom: 12px;
}

.link-search-input {
  width: 100%;
}

.wholesaler-list {
  max-height: 200px;
  overflow-y: auto;
}

.wholesaler-option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.2s;
}

.wholesaler-option:hover:not(.disabled) {
  background: #f0f5ff;
}

.wholesaler-option.disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.wholesaler-option-info {
  display: flex;
  align-items: center;
  gap: 8px;
}

.wholesaler-option-name {
  font-size: 14px;
  color: #333;
}

.wholesaler-option-code {
  font-size: 12px;
  color: #999;
}

.linked-badge {
  font-size: 12px;
  color: #999;
  background: #f0f0f0;
  padding: 2px 8px;
  border-radius: 4px;
}

.add-badge {
  font-size: 12px;
  color: #1890ff;
  background: #e6f7ff;
  padding: 2px 8px;
  border-radius: 4px;
}
</style>
